// Потенциални — the split list + detail that is the primary screen (04 §2).

import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { LeadDetail } from './LeadDetail.js';
import { LeadFilters, useLeadFilters, type LeadFilterState } from './LeadFilters.js';
import { ListExport } from './export/ExportButtons.js';
import { LeadsTable } from './LeadsTable.js';
import { useNicheFilterOptions } from './NicheFilterFields.js';
import { useLeads, type LeadListRow } from './useLeads.js';
import './leads.css';

function LeadCard({
  lead,
  selected,
  onSelect,
}: {
  lead: LeadListRow;
  selected: boolean;
  onSelect: (id: number) => void;
}) {
  const tierClass =
    lead.tier === 'A' ? 'badge--tier-a' : lead.tier === 'B' ? 'badge--tier-b' : 'badge--tier-c';

  return (
    <div
      role="button"
      tabIndex={0}
      className={`lead-card${selected ? ' lead-card--selected' : ''}`}
      onClick={() => onSelect(lead.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(lead.id);
        }
      }}
      aria-current={selected}
    >
      <div className="lead-card__top">
        <span className="lead-card__name">{lead.business_name}</span>
        {lead.lead_score !== null && (
          <span className="badge badge--score">{lead.lead_score} т.</span>
        )}
      </div>

      <div className="lead-card__mid">
        <span className="lead-card__location">{lead.area ?? lead.city_key}</span>
        {lead.tier && <span className={`badge ${tierClass}`}>Tier {lead.tier}</span>}
        {lead.priority && <span className="badge badge--neutral">{lead.priority}</span>}
      </div>

      <div className="lead-card__bottom">
        {lead.domain_norm ? (
          <span className="lead-card__domain">{lead.domain_norm}</span>
        ) : (
          <span className="lead-card__nowebsite">без уебсайт</span>
        )}

        {lead.branchCount !== undefined && lead.branchCount > 0 ? (
          <span className="lead-card__branchtag">🏢 +{lead.branchCount}</span>
        ) : null}

        {lead.do_not_contact && <span className="badge badge--danger">Не безпокой</span>}
      </div>
    </div>
  );
}

export function LeadsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const selected = id === undefined ? null : Number(id);
  const { filters, setFilters, apply } = useLeadFilters();
  const { data: all = [], isLoading, error } = useLeads(filters.search);
  const leads = apply(all);
  const niche = useNicheFilterOptions(all);
  const isTable = params.get('view') === 'table';

  // If table view is chosen
  if (isTable) {
    return (
      <div className="leads leads--table">
        <div className="leads__toolbar">
          <div className="leads__toolleft">
            <LeadFilters
              filters={filters}
              onChange={setFilters}
              count={leads.length}
              niche={niche}
            />
          </div>
          <div className="leads__toolright">
            <div className="tabs-nav">
              <button type="button" className="tabs-nav__btn" onClick={() => setParams({})}>
                Разделен изглед
              </button>
              <button type="button" className="tabs-nav__btn tabs-nav__btn--active">
                Таблица
              </button>
            </div>
            <ListExport leads={leads} label="potentsialni" />
          </div>
        </div>
        <div className="leads__tablewrap">
          <LeadsTable leads={leads} />
        </div>
      </div>
    );
  }

  // Split View
  return (
    <div className="leads">
      <aside className="leads__list">
        <div className="leads__listheader">
          <LeadFilters filters={filters} onChange={setFilters} count={leads.length} niche={niche} />
          <div className="leads__listcontrols">
            <select
              className="select select--sm"
              value={filters.sort}
              onChange={(e) =>
                setFilters({ ...filters, sort: e.target.value as LeadFilterState['sort'] })
              }
              aria-label="Подредба"
            >
              <option value="score">По точки (низх.)</option>
              <option value="name">По име (А-Я)</option>
              <option value="newest">Най-нови първо</option>
            </select>

            <div className="tabs-nav">
              <button
                type="button"
                className="tabs-nav__btn tabs-nav__btn--active"
                title="Разделен изглед"
              >
                Списък
              </button>
              <button
                type="button"
                className="tabs-nav__btn"
                onClick={() => setParams({ view: 'table' })}
                title="Табличен изглед"
              >
                Таблица
              </button>
            </div>

            <ListExport leads={leads} label="potentsialni" />
          </div>
        </div>

        <div className="leads__cardscontainer">
          {isLoading && <p className="leads__msg">Зареждане на обекти…</p>}
          {error && <p className="leads__msg leads__msg--err">{(error as Error).message}</p>}
          {!isLoading && !error && leads.length === 0 && (
            <div className="empty-state">
              <p className="empty-state__title">Няма намерени обекти</p>
              <p className="empty-state__desc">
                Променете критериите за филтриране или пуснете Откриване от Пайплайн.
              </p>
            </div>
          )}

          {leads.map((lead) => (
            <LeadCard
              key={lead.id}
              lead={lead}
              selected={lead.id === selected}
              onSelect={(leadId) => navigate(`/leads/${leadId}`)}
            />
          ))}
        </div>
      </aside>

      <section className="leads__detail">
        <LeadDetail leadId={selected ?? leads[0]?.id ?? null} />
      </section>
    </div>
  );
}
